import { useCallback, useEffect, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import { showModal } from 'modal.react.js';
import { useSelector } from 'react-redux';

import { AModal } from 'components/shared/AModal';
import area from 'components/shared/GlobalModalArea/area';
import AlertModal from 'components/shared/GlobalModalArea/Modal/AlertModal';
import { useSnackbar } from 'hooks/useSnackbar';
import i18n from 'i18n';
import { itinerariesItemDataSelector } from 'store/itinerariesItem/selectors';
import api from 'utils/axios';
import { updateArrayById } from 'utils/immutableFunctions';
import { getGuestNameInfo } from 'utils/itineraries/functions';
import {
  useItinerariesItemNextStepFormHandler,
  useItinerariesItemSaveHandler,
} from 'utils/itineraries/functions/builderUtils';
import logger from 'utils/logger';

import GuestForm from './GuestForm';
import GuestSearchForm from './GuestSearchForm';

const initialFormState = {
  values: {},
  hasValidationErrors: false,
  pristine: true,
  submitting: false,
  form: null,
};

export const insertGuestName = (guest, { leftSpace = false, rightSpace = false } = {}) => {
  const info = getGuestNameInfo(guest);
  if (info.isEmpty) {
    return '';
  }

  return `${leftSpace ? ' ' : ''}'${info.name}'${rightSpace ? ' ' : ''}`;
};

export const buildGuestActionMessage =
  (action) =>
  (guest, translate = false) => {
    const guestName = insertGuestName(guest, { rightSpace: true });

    if (translate) {
      return i18n.t(`proposal_content.guest_portal.guest_form.snackbar.guest_${action}`, { guest_name: guestName });
    }

    return `Guest ${guestName}has been ${action}.`;
  };

export const useGuestHandlers = () => {
  const [mainFormState, setMainFormState] = useState(initialFormState);
  const guestPortal = useSelector(itinerariesItemDataSelector);
  const guests = mainFormState.values?.guests ?? [];
  const onSnackbar = useSnackbar();
  const handleNextExternal = useItinerariesItemNextStepFormHandler();
  const handleSaveExternal = useItinerariesItemSaveHandler({ showSuccessSnackbar: false });
  const handleSaveExternalWithSnackbars = useItinerariesItemSaveHandler();

  const handleSaveGuestPortal = useCallback(
    async (updatedGuests) => {
      await handleSaveExternal({ ...mainFormState.values, guests: updatedGuests });
    },
    [handleSaveExternal, mainFormState.values],
  );

  const handleDeleteGuest = useCallback(
    (guestToDelete) => {
      return showModal(area, (res) => (
        <AlertModal
          headerText="Delete Guest"
          confirmButtonText="Delete"
          closeButtonText="Cancel"
          res={res}
          onAccept={async () => {
            try {
              const updatedGuests = updateArrayById(guests, guestToDelete.id, (guest) => ({
                ...guest,
                _destroy: true,
              }));
              await handleSaveGuestPortal(updatedGuests);

              onSnackbar(buildGuestActionMessage('deleted')(guestToDelete));
              res();
            } catch (err) {
              onSnackbar("Couldn't delete the guest. Please try again later.", true);
            }
          }}
          bodyText="Do you want to delete this guest now?"
        />
      ));
    },
    [guests, handleSaveGuestPortal, onSnackbar],
  );

  const handleAddGuests = useCallback(
    async (newGuests) => {
      try {
        // For multiple guest addition from GuestSearchForm
        if (Array.isArray(newGuests)) {
          await handleSaveGuestPortal([...guests, ...newGuests]);
        } else {
          await handleSaveGuestPortal([...guests, newGuests]);
          onSnackbar(buildGuestActionMessage('added')(newGuests));
        }
      } catch (err) {
        logger.error(err);
        onSnackbar("Couldn't add new guest. Please try again later.", true);
        return err;
      }
    },
    [handleSaveGuestPortal, guests, onSnackbar],
  );

  const handleUpdateGuest = async (values) => {
    try {
      await handleSaveGuestPortal(updateArrayById(guests, values.id, () => values));
      onSnackbar(buildGuestActionMessage('updated')(values));
    } catch (err) {
      logger.error(err);
      onSnackbar("Couldn't update the guest. Please try again later.", true);
      return err;
    }
  };

  const handleClickAddNewGuestButton = () => {
    openGuestFormModal();
  };

  const handleClickEditGuestButton = (guest) => {
    openGuestFormModal(guest);
  };

  const handleClickGuestSearch = () => {
    openGuestSearchModal();
  };

  const handleSaveAllForm = useCallback(async () => {
    await handleSaveExternalWithSnackbars(mainFormState.values);
  }, [mainFormState]);

  const openGuestFormModal = (guest) => {
    return showModal(area, (res, err) => (
      <AModal header={guest ? 'Edit Guest' : 'Add Guest'} isOpen onClose={() => err(null)}>
        <GuestForm
          guest={guest}
          onSubmit={async (values) => {
            try {
              const err = guest ? await handleUpdateGuest(values) : await handleAddGuests(values);

              if (!err) {
                res(false);
              }
            } catch (err) {
              return err;
            }
          }}
          travelEndDate={guestPortal?.itinerary?.travelEndDate}
        />
      </AModal>
    ));
  };

  const openGuestSearchModal = () => {
    return showModal(area, (res) => (
      <GuestSearchForm onSubmit={handleAddGuests} onClose={() => res(false)} guestPortalId={guestPortal.id} />
    ));
  };

  return {
    guestPortal,
    handleDeleteGuest,
    handleNext: handleNextExternal,
    mainFormState,
    handleUpdateMainFormState: setMainFormState,
    handleClickAddNewGuestButton,
    handleClickEditGuestButton,
    handleClickGuestSearch,
    handleSaveAllForm,
  };
};

export const useCountries = (defaultCountry) => {
  const [states, setStates] = useState([]);

  const { data: countries, refetch: fetchCountries } = useQuery(
    ['refs', 'countries'],
    () =>
      api
        .get('/refs/countries', { params: { language: i18n.locale } })
        .then(({ data }) => Object.entries(data).map(([key, value]) => ({ value: key.toUpperCase(), label: value }))),
    { enabled: false },
  );

  const onCountryChange = async (code) => {
    if (!code) {
      setStates([]);
      return;
    }

    try {
      const res = await api.get(`/refs/countries/${code}/states`, { params: { language: i18n.locale } });
      const handledRes = Object.entries(res.data).map(([key, value]) => ({ value: key.toUpperCase(), label: value }));
      setStates(handledRes);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchCountries().then(() => {
      if (defaultCountry) {
        onCountryChange(defaultCountry);
      }
    });
  }, [defaultCountry]);

  return {
    countries,
    states,
    onCountryChange,
  };
};
